import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { PageContainer } from "@/layouts/PageContainer";
import { Share2, ChevronLeft, AlertCircle, BookOpen } from "lucide-react";

export default function ClassShares() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, sharesRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/class-shares`, { headers }),
      ]);
      if (!sharesRes.ok) {
        const body = await sharesRes.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load class shares");
      }
      if (schoolRes.ok) setSchool(await schoolRes.json());
      const data = await sharesRes.json();
      setShares(data.shares ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/schools"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Share2 size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">
            Class Sharing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {shares.length} class{shares.length !== 1 ? "es" : ""} with active sharing
          </p>
        </div>
      </div>

      {shares.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20">
          <Share2 size={28} className="text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No active class sharing in this school
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {shares.map((share) => (
            <div
              key={share.groupId}
              className="bg-card border border-border rounded-xl px-5 py-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={14} className="text-muted-foreground shrink-0" />
                <span className="text-sm font-semibold text-foreground">
                  {share.groupName || "Unnamed Class"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Owner:{" "}
                <span className="font-medium text-foreground">
                  {share.ownerName || "-"}
                </span>
                {share.ownerEmail && (
                  <span className="ml-1">({share.ownerEmail})</span>
                )}
              </p>
              {share.sharedWith?.length > 0 ? (
                <div className="space-y-1.5">
                  {share.sharedWith.map((sw) => (
                    <div
                      key={String(sw.userId)}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-foreground font-medium">
                        {sw.name || sw.email || "Unknown"}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[0.7rem] font-medium ${
                          sw.permission === "edit"
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {sw.permission}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No members</p>
              )}
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
